import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { MessageSquare, Calendar, Mail, X, ChevronUp, ChevronDown, MapPin, Clock, Phone, Video, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { format, addDays, isWeekend, isBefore, startOfDay, isToday } from 'date-fns';
import { cn } from '@/lib/utils';

interface AccountRep {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface TimeSlot {
  time: string;
  label: string;
  available: boolean;
}

interface ScheduledVisit {
  id: string;
  scheduledDate: string;
  scheduledTime: string;
  meetingType: string;
  status: string;
  purpose?: string;
}

type MeetingType = 'in-person' | 'phone' | 'video';

const ZOOM_LINK = "https://us02web.zoom.us/j/8314285040?pwd=cDkwTE5aN2FmcWVIRC8ySnlCSFozdz09";
const ZOOM_MEETING_ID = "831 428 5040";
const ZOOM_PASSCODE = "sj";

export function ContactWidget() {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [meetingType, setMeetingType] = useState<MeetingType>('in-person');
  const [customerPhone, setCustomerPhone] = useState('');
  const [purpose, setPurpose] = useState('');
  const { toast } = useToast();

  // Hide widget on splash page
  if (location === '/splash') {
    return null;
  }

  const { data: accountRepData } = useQuery<{ rep: AccountRep }>({
    queryKey: ['/api/account-rep'],
    enabled: isOpen,
  });

  const { data: visitsData } = useQuery<{ visits: ScheduledVisit[] }>({
    queryKey: ['/api/visits'],
    enabled: isOpen,
  });

  const { data: slotsData, refetch: refetchSlots } = useQuery<{ slots: TimeSlot[], message?: string }>({
    queryKey: ['/api/visits/available-slots', selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''],
    enabled: !!selectedDate,
  });

  const scheduleMutation = useMutation({
    mutationFn: async (data: { 
      scheduledDate: string; 
      scheduledTime: string; 
      meetingType: MeetingType;
      customerPhone?: string;
      purpose: string 
    }) => {
      return apiRequest('POST', '/api/visits', data);
    },
    onSuccess: () => {
      toast({
        title: "Appointment Scheduled!",
        description: "You'll receive a calendar invite shortly.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/visits'] });
      setSelectedDate(undefined);
      setSelectedTime('');
      setMeetingType('in-person');
      setCustomerPhone('');
      setPurpose('');
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to schedule appointment",
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (visitId: string) => {
      return apiRequest('POST', `/api/visits/${visitId}/cancel`, {});
    },
    onSuccess: () => {
      toast({
        title: "Appointment Cancelled",
        description: "Your appointment has been cancelled.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/visits'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel appointment",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (selectedDate) {
      refetchSlots();
      setSelectedTime('');
    }
  }, [selectedDate, refetchSlots]);

  const rep = accountRepData?.rep || {
    firstName: 'Jimmy',
    lastName: 'Vosika',
    email: 'jimmy@shopjimmy.com',
  };

  const upcomingVisits = (visitsData?.visits || []).filter(
    v => v.status === 'PENDING' || v.status === 'CONFIRMED'
  );

  const handleSchedule = () => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Missing Information",
        description: "Please select both a date and time.",
        variant: "destructive",
      });
      return;
    }

    if (meetingType === 'phone' && !customerPhone.trim()) {
      toast({
        title: "Phone Number Required",
        description: "Please enter your phone number so we can call you.",
        variant: "destructive",
      });
      return;
    }

    scheduleMutation.mutate({
      scheduledDate: format(selectedDate, 'yyyy-MM-dd'),
      scheduledTime: selectedTime,
      meetingType,
      customerPhone: meetingType === 'phone' ? customerPhone : undefined,
      purpose,
    });
  };

  const disabledDays = (date: Date) => {
    const today = startOfDay(new Date());
    return isWeekend(date) || isBefore(date, today);
  };

  const getMeetingTypeLabel = (type: string) => {
    switch (type) {
      case 'in-person': return 'In-Person';
      case 'phone': return 'Phone Call';
      case 'video': return 'Video Call';
      default: return type;
    }
  };

  const getMeetingTypeIcon = (type: string) => {
    switch (type) {
      case 'in-person': return <Users className="h-3 w-3" />;
      case 'phone': return <Phone className="h-3 w-3" />;
      case 'video': return <Video className="h-3 w-3" />;
      default: return null;
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-1 bg-gray-400 text-white p-3 rounded-full shadow-lg hover:bg-gray-500 transition-all hover:scale-105"
        data-testid="contact-widget-toggle"
        aria-label="Contact us or schedule an appointment"
      >
        <MessageSquare className="h-5 w-5" />
        <Calendar className="h-5 w-5" />
      </button>
    );
  }

  return (
    <Card className={cn(
      "fixed bottom-6 right-6 z-50 w-[400px] shadow-2xl transition-all duration-200",
      isMinimized ? "h-auto" : "max-h-[650px]"
    )} data-testid="contact-widget-panel">
      <CardHeader className="pb-2 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Schedule an Appointment
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsMinimized(!isMinimized)}
              data-testid="minimize-widget"
            >
              {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
              data-testid="close-widget"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {!isMinimized && (
        <CardContent className="pt-4 max-h-[570px] overflow-y-auto space-y-4">
          {/* Rep Info - Compact at top */}
          <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Account Manager</p>
                <p className="text-sm font-medium">{rep.firstName} {rep.lastName}</p>
                <a 
                  href={`mailto:${rep.email}`} 
                  className="text-sm text-primary hover:underline"
                  data-testid="email-rep"
                >
                  {rep.email}
                </a>
              </div>
            </div>
          </div>

          {/* Upcoming Appointments */}
          {upcomingVisits.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 text-sm mb-2">
                Upcoming Appointments
              </h4>
              {upcomingVisits.map(visit => (
                <div key={visit.id} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    {getMeetingTypeIcon(visit.meetingType)}
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      {format(new Date(visit.scheduledDate + 'T12:00:00'), 'MMM d')} at {visit.scheduledTime}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={visit.status === 'CONFIRMED' ? 'default' : 'secondary'} className="text-xs">
                      {getMeetingTypeLabel(visit.meetingType)}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => cancelMutation.mutate(visit.id)}
                      data-testid={`cancel-visit-${visit.id}`}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Meeting Type Selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Appointment Type</Label>
            <RadioGroup
              value={meetingType}
              onValueChange={(v) => setMeetingType(v as MeetingType)}
              className="grid grid-cols-3 gap-2"
            >
              <div className="flex items-center">
                <RadioGroupItem value="in-person" id="in-person" className="peer sr-only" />
                <Label
                  htmlFor="in-person"
                  className="flex flex-col items-center justify-center w-full py-2 px-1 rounded-lg border-2 cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 hover:bg-muted/50 transition-colors"
                >
                  <Users className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">In-Person</span>
                </Label>
              </div>
              <div className="flex items-center">
                <RadioGroupItem value="phone" id="phone" className="peer sr-only" />
                <Label
                  htmlFor="phone"
                  className="flex flex-col items-center justify-center w-full py-2 px-1 rounded-lg border-2 cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 hover:bg-muted/50 transition-colors"
                >
                  <Phone className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Phone Call</span>
                </Label>
              </div>
              <div className="flex items-center">
                <RadioGroupItem value="video" id="video" className="peer sr-only" />
                <Label
                  htmlFor="video"
                  className="flex flex-col items-center justify-center w-full py-2 px-1 rounded-lg border-2 cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 hover:bg-muted/50 transition-colors"
                >
                  <Video className="h-5 w-5 mb-1" />
                  <span className="text-xs font-medium">Video Call</span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Phone Input for Phone Calls */}
          {meetingType === 'phone' && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Your Phone Number</Label>
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                data-testid="customer-phone-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                We'll call you at this number at your scheduled time.
              </p>
            </div>
          )}

          {/* Zoom Info for Video Calls */}
          {meetingType === 'video' && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <p className="text-sm font-medium text-purple-800 dark:text-purple-200 mb-1">
                Zoom Meeting Details
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300">
                Meeting ID: {ZOOM_MEETING_ID} • Passcode: {ZOOM_PASSCODE}
              </p>
              <a
                href={ZOOM_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
              >
                Join meeting link →
              </a>
            </div>
          )}

          {/* Calendar */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Select a Date</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Monday - Friday, 9:00 AM - 2:00 PM CST
            </p>
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              disabled={disabledDays}
              className="rounded-md border"
              fromDate={new Date()}
              toDate={addDays(new Date(), 60)}
              modifiers={{
                today: (date) => isToday(date),
              }}
              modifiersClassNames={{
                today: "border-2 border-primary font-bold",
                selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
              }}
            />
          </div>
          
          {/* Time Slots */}
          {selectedDate && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Select a Time</Label>
              {slotsData?.message ? (
                <p className="text-sm text-muted-foreground">{slotsData.message}</p>
              ) : (slotsData?.slots?.length || 0) === 0 ? (
                <p className="text-sm text-muted-foreground">Loading time slots...</p>
              ) : (
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger data-testid="time-slot-select">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {(slotsData?.slots || []).map(slot => (
                      <SelectItem
                        key={slot.time}
                        value={slot.time}
                        disabled={!slot.available}
                        data-testid={`time-slot-${slot.time}`}
                      >
                        {slot.label} {!slot.available && '(Booked)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          
          {/* Purpose/Notes */}
          {selectedDate && selectedTime && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Notes (Optional)</Label>
              <Textarea
                placeholder={meetingType === 'in-person' 
                  ? "What products are you interested in viewing?"
                  : "What would you like to discuss?"
                }
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="resize-none"
                rows={2}
                data-testid="visit-purpose"
              />
            </div>
          )}
          
          {/* Submit Button */}
          {selectedDate && selectedTime && (
            <Button
              onClick={handleSchedule}
              disabled={scheduleMutation.isPending}
              className="w-full"
              data-testid="submit-visit"
            >
              {scheduleMutation.isPending ? 'Scheduling...' : `Schedule ${getMeetingTypeLabel(meetingType)}`}
            </Button>
          )}
          
          {/* Location Info - Only show for in-person */}
          {meetingType === 'in-person' && (
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">ShopJimmy.com</p>
                  <p className="text-muted-foreground">2300 West Highway 13</p>
                  <p className="text-muted-foreground">Burnsville, MN 55337</p>
                </div>
              </div>
              <div className="flex items-start gap-2 mt-2">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-muted-foreground">Mon-Fri: 9:00 AM - 2:00 PM CST</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
